import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Fuel,
  TrendingUp,
  TrendingDown,
  MapPin,
  RefreshCw,
  Settings,
  Filter,
  Mail,
  Upload,
  AlertTriangle,
  CheckCircle,
  Clock,
  Calculator,
  X,
} from 'lucide-react';
import { useAppStore, usePricingStore, type GasBuddyCompetitor } from '../store';
import { formatFuelPrice, formatMargin, formatDistance, getCompetitorColor, formatRelativeTime } from '@jrd/core';
import { fetchStationPricesFromAPI, fetchCompetitorsFromAPI } from '../services/gasbuddy';
import { processPriceNoticeAttachment, parsePriceNotice, toWholesalePrice } from '../services/priceNotice';

const PRICE_O_TRON_ASCII = `
██████╗ ██████╗ ██╗ ██████╗███████╗     ██████╗     ████████╗██████╗  ██████╗ ███╗   ██╗
██╔══██╗██╔══██╗██║██╔════╝██╔════╝    ██╔═══██╗    ╚══██╔══╝██╔══██╗██╔═══██╗████╗  ██║
██████╔╝██████╔╝██║██║     █████╗█████╗██║   ██║█████╗ ██║   ██████╔╝██║   ██║██╔██╗ ██║
██╔═══╝ ██╔══██╗██║██║     ██╔══╝╚════╝██║   ██║╚════╝ ██║   ██╔══██╗██║   ██║██║╚██╗██║
██║     ██║  ██║██║╚██████╗███████╗    ╚██████╔╝       ██║   ██║  ██║╚██████╔╝██║ ╚████║
╚═╝     ╚═╝  ╚═╝╚═╝ ╚═════╝╚══════╝     ╚═════╝        ╚═╝   ╚═╝  ╚═╝ ╚═════╝ ╚═╝  ╚═══╝
`;

// Modal for email configuration
function EmailConfigModal({
  isOpen,
  onClose,
  locationId,
}: {
  isOpen: boolean;
  onClose: () => void;
  locationId: string;
}) {
  const { emailConfigs, setEmailConfig } = usePricingStore();
  const existingConfig = emailConfigs[locationId];

  const [email, setEmail] = useState(existingConfig?.email || '');
  const [folder, setFolder] = useState(existingConfig?.folder || 'Inbox');
  const [subjectFilter, setSubjectFilter] = useState(existingConfig?.subjectFilter || 'price notice');
  const [autoProcess, setAutoProcess] = useState(existingConfig?.autoProcess ?? true);

  const handleSave = () => {
    setEmailConfig(locationId, { email, folder, subjectFilter, autoProcess });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="card max-w-md w-full mx-4"
      >
        <div className="card-header flex items-center justify-between">
          <span>// EMAIL PRICE NOTICE CONFIG</span>
          <button onClick={onClose} className="text-text-muted hover:text-text-primary">×</button>
        </div>
        <div className="card-body space-y-4">
          <p className="text-text-secondary text-sm">
            Configure email monitoring to automatically extract wholesale costs from price notice attachments.
          </p>

          <div>
            <label className="block text-text-secondary text-xs mb-1">Email Address</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="holiday3851@jrdcompanies.com"
              className="w-full bg-bg-input border border-text-muted/30 rounded px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label className="block text-text-secondary text-xs mb-1">Folder</label>
            <input
              type="text"
              value={folder}
              onChange={(e) => setFolder(e.target.value)}
              placeholder="Inbox"
              className="w-full bg-bg-input border border-text-muted/30 rounded px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label className="block text-text-secondary text-xs mb-1">Subject Filter</label>
            <input
              type="text"
              value={subjectFilter}
              onChange={(e) => setSubjectFilter(e.target.value)}
              placeholder="price notice"
              className="w-full bg-bg-input border border-text-muted/30 rounded px-3 py-2 text-sm"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="autoProcess"
              checked={autoProcess}
              onChange={(e) => setAutoProcess(e.target.checked)}
              className="rounded border-text-muted/30"
            />
            <label htmlFor="autoProcess" className="text-text-secondary text-sm">
              Auto-process new emails
            </label>
          </div>

          <div className="flex gap-3 pt-2">
            <button onClick={onClose} className="btn-ghost flex-1">Cancel</button>
            <button onClick={handleSave} className="btn-primary flex-1">Save Config</button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// Price Simulator Modal - Edit 87 and see how other grades follow
function PriceSimulatorModal({
  isOpen,
  onClose,
  currentPrices,
  wholesaleCost,
}: {
  isOpen: boolean;
  onClose: () => void;
  currentPrices: { grade: string; price: number }[];
  wholesaleCost: number;
}) {
  const current87 = currentPrices.find((p) => p.grade === '87')?.price || 0;
  const [simulated87, setSimulated87] = useState(current87.toFixed(3));

  // Standard grade spreads (typical in MN market)
  const SPREAD_89 = 0.10; // +10¢ over 87
  const SPREAD_91 = 0.30; // +30¢ over 87

  const sim87 = parseFloat(simulated87) || 0;
  const sim89 = sim87 + SPREAD_89;
  const sim91 = sim87 + SPREAD_91;

  const margin87 = wholesaleCost > 0 ? sim87 - wholesaleCost : 0;
  const priceChange = sim87 - current87;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="card max-w-lg w-full mx-4"
      >
        <div className="card-header flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Calculator className="w-4 h-4 text-jrd-orange" />
            // PRICE SIMULATOR
          </span>
          <button onClick={onClose} className="text-text-muted hover:text-text-primary">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="card-body space-y-6">
          <p className="text-text-secondary text-sm">
            Adjust 87 Unleaded price and see how other grades follow based on standard market spreads.
          </p>

          {/* 87 Unleaded Input */}
          <div className="p-4 bg-jrd-orange/10 rounded-lg border border-jrd-orange/30">
            <label className="block text-text-muted text-xs mb-2">87 UNLEADED (EDITABLE)</label>
            <div className="flex items-center gap-3">
              <span className="text-2xl text-text-muted">$</span>
              <input
                type="number"
                step="0.001"
                value={simulated87}
                onChange={(e) => setSimulated87(e.target.value)}
                className="flex-1 bg-bg-darker border border-jrd-orange/50 rounded px-4 py-3 text-3xl font-bold text-jrd-orange font-mono text-center"
              />
              <span className="text-lg text-text-muted">⁹</span>
            </div>
            {priceChange !== 0 && (
              <div className={`mt-2 text-sm flex items-center gap-1 ${priceChange > 0 ? 'text-green-400' : 'text-red-400'}`}>
                {priceChange > 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                {priceChange > 0 ? '+' : ''}${priceChange.toFixed(3)} from current
              </div>
            )}
          </div>

          {/* Simulated Grades */}
          <div className="grid grid-cols-2 gap-4">
            {[
              { grade: '89', price: sim89, spread: `+$${SPREAD_89.toFixed(2)}` },
              { grade: '91', price: sim91, spread: `+$${SPREAD_91.toFixed(2)}` },
            ].map(({ grade, price, spread }) => (
              <div key={grade} className="p-3 bg-bg-input rounded border border-text-muted/20">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-text-muted text-xs">{grade}</span>
                  <span className="text-text-muted text-xs">{spread}</span>
                </div>
                <div className="text-xl font-bold text-text-primary font-mono">
                  ${price.toFixed(3)}⁹
                </div>
              </div>
            ))}

            {/* Margin Display */}
            <div className={`p-3 rounded border ${margin87 > 0 ? 'bg-jrd-green/10 border-jrd-green/30' : 'bg-red-500/10 border-red-500/30'}`}>
              <div className="text-text-muted text-xs mb-1">87 MARGIN</div>
              <div className={`text-xl font-bold font-mono ${margin87 > 0 ? 'text-jrd-green' : 'text-red-400'}`}>
                ${margin87.toFixed(3)}
              </div>
              <div className="text-text-muted text-xs mt-1">
                Per gallon profit
              </div>
            </div>
          </div>

          {/* Wholesale Reference */}
          {wholesaleCost > 0 && (
            <div className="p-3 bg-bg-input rounded border border-text-muted/20 flex justify-between items-center">
              <span className="text-text-muted text-sm">Wholesale Cost (87)</span>
              <span className="text-text-primary font-mono">${wholesaleCost.toFixed(3)}</span>
            </div>
          )}

          {/* Margin Bar */}
          <div className="p-4 bg-bg-input rounded-lg border border-text-muted/20">
            <div className="flex items-center justify-between mb-2">
              <span className="text-text-secondary text-sm">Projected Margin</span>
              <span className={`font-mono font-bold ${margin87 >= 0.10 ? 'text-jrd-green' : margin87 > 0 ? 'text-yellow-400' : 'text-red-400'}`}>
                {wholesaleCost > 0 ? `$${margin87.toFixed(3)}` : 'Set wholesale cost'}
              </span>
            </div>
            <div className="h-4 bg-bg-darker rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${Math.min(Math.max((margin87 / 0.30) * 100, 0), 100)}%` }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
                className={`h-full ${margin87 >= 0.15 ? 'bg-gradient-to-r from-jrd-orange to-jrd-green' : margin87 > 0 ? 'bg-yellow-500' : 'bg-red-500'}`}
              />
            </div>
            <div className="flex items-center justify-between mt-2 text-xs text-text-muted">
              <span>$0.00</span>
              <span className="text-jrd-green">Target: $0.15+</span>
              <span>$0.30</span>
            </div>
          </div>

          <div className="flex gap-3">
            <button onClick={onClose} className="btn-ghost flex-1">Close</button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export function PriceOTron() {
  const { locations } = useAppStore();
  const {
    stationPrices,
    setStationPrices,
    competitors,
    setCompetitors,
    wholesalePrices,
    setWholesalePrice,
    emailConfigs,
    isLoadingPrices,
    setIsLoadingPrices,
    isLoadingCompetitors,
    setIsLoadingCompetitors,
    pricesLastUpdated,
    competitorsLastUpdated,
  } = usePricingStore();

  const gasLocations = locations.filter((l) => l.industry === 'gas' || l.industry === 'both');
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(
    gasLocations[0]?.id || null
  );
  const [pricingStrategy, setPricingStrategy] = useState<'match' | 'undercut' | 'premium'>('match');
  const [showEmailConfig, setShowEmailConfig] = useState(false);
  const [showSimulator, setShowSimulator] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'processing' | 'success' | 'error'>('idle');
  const [uploadMessage, setUploadMessage] = useState('');

  const selectedLocation = gasLocations.find((l) => l.id === selectedLocationId);
  const currentPrices = selectedLocation?.gasbuddyId
    ? stationPrices[selectedLocation.gasbuddyId]
    : null;
  const currentWholesale = selectedLocationId ? wholesalePrices[selectedLocationId] : null;

  // Calculate margin
  const retailPrice87 = currentPrices?.prices.find((p) => p.grade === '87')?.price || 0;
  const wholesaleCost87 = currentWholesale?.cost || 0;
  const margin87 = retailPrice87 > 0 && wholesaleCost87 > 0 ? retailPrice87 - wholesaleCost87 : 0;

  // Fetch prices from GasBuddy
  const refreshPrices = useCallback(async () => {
    if (!selectedLocation?.gasbuddyId) return;

    setIsLoadingPrices(true);
    try {
      const prices = await fetchStationPricesFromAPI(selectedLocation.gasbuddyId);
      if (prices) {
        setStationPrices(selectedLocation.gasbuddyId, prices);
      }
    } catch (error) {
      console.error('Failed to fetch prices:', error);
    } finally {
      setIsLoadingPrices(false);
    }
  }, [selectedLocation?.gasbuddyId, setIsLoadingPrices, setStationPrices]);

  // Fetch competitors from GasBuddy
  const refreshCompetitors = useCallback(async () => {
    if (!selectedLocation?.lat || !selectedLocation?.lng) return;

    setIsLoadingCompetitors(true);
    try {
      const nearby = await fetchCompetitorsFromAPI(selectedLocation.lat, selectedLocation.lng, 10);
      // Filter out our own station
      const filtered = nearby.filter((s) => s.id !== selectedLocation.gasbuddyId);
      setCompetitors(filtered);
    } catch (error) {
      console.error('Failed to fetch competitors:', error);
    } finally {
      setIsLoadingCompetitors(false);
    }
  }, [selectedLocation, setIsLoadingCompetitors, setCompetitors]);

  // Handle price notice file upload
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !selectedLocationId) return;

    setUploadStatus('processing');
    setUploadMessage('Parsing price notice...');

    try {
      const notice = await processPriceNoticeAttachment(file);
      if (notice) {
        const wholesale = toWholesalePrice(selectedLocationId, notice, '87');
        if (wholesale) {
          setWholesalePrice(selectedLocationId, wholesale);
          setUploadStatus('success');
          setUploadMessage(
            `Updated: ${notice.supplier} - $${wholesale.cost.toFixed(3)}/gal (effective ${notice.effectiveDate.toLocaleDateString()})`
          );
        } else {
          setUploadStatus('error');
          setUploadMessage('Could not extract 87 unleaded cost from notice');
        }
      } else {
        setUploadStatus('error');
        setUploadMessage('Failed to parse price notice');
      }
    } catch (error) {
      setUploadStatus('error');
      setUploadMessage('Error processing file');
      console.error(error);
    }

    // Reset file input
    event.target.value = '';

    // Clear status after 5 seconds
    setTimeout(() => {
      setUploadStatus('idle');
      setUploadMessage('');
    }, 5000);
  };

  // Auto-refresh on mount and location change
  useEffect(() => {
    if (selectedLocation?.gasbuddyId) {
      refreshPrices();
      refreshCompetitors();
    }
  }, [selectedLocation?.gasbuddyId, refreshPrices, refreshCompetitors]);

  const handleRefreshAll = async () => {
    await Promise.all([refreshPrices(), refreshCompetitors()]);
  };

  // No locations configured
  if (gasLocations.length === 0) {
    return (
      <div className="p-6">
        <div className="flex items-start justify-between mb-6">
          <div>
            <pre className="text-jrd-orange text-[6px] sm:text-[8px] font-mono leading-none hidden md:block">
              {PRICE_O_TRON_ASCII}
            </pre>
            <h1 className="text-2xl font-bold text-text-primary md:hidden">Price-O-Tron</h1>
            <p className="text-text-muted text-sm font-mono mt-2">
              <span className="text-jrd-orange">//</span> Real-time fuel pricing intelligence
            </p>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card"
        >
          <div className="card-body py-16 text-center">
            <Fuel className="w-16 h-16 mx-auto text-text-muted/50 mb-4" />
            <h2 className="text-xl font-bold text-text-primary mb-2">No Gas Stations Configured</h2>
            <p className="text-text-secondary mb-6 max-w-md mx-auto">
              Add a gas station location with a GasBuddy ID to start tracking real-time fuel prices,
              competitor pricing, and calculate accurate margins.
            </p>
            <a href="/settings" className="btn-primary inline-flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Configure Location
            </a>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header with ASCII logo */}
      <div className="flex items-start justify-between">
        <div>
          <pre className="text-jrd-orange text-[6px] sm:text-[8px] font-mono leading-none hidden md:block">
            {PRICE_O_TRON_ASCII}
          </pre>
          <h1 className="text-2xl font-bold text-text-primary md:hidden">Price-O-Tron</h1>
          <p className="text-text-muted text-sm font-mono mt-2">
            <span className="text-jrd-orange">//</span> Real-time fuel pricing intelligence
          </p>
        </div>

        <button
          onClick={handleRefreshAll}
          disabled={isLoadingPrices || isLoadingCompetitors}
          className="btn-ghost flex items-center gap-2"
        >
          <RefreshCw
            className={`w-4 h-4 ${isLoadingPrices || isLoadingCompetitors ? 'animate-spin' : ''}`}
          />
          <span className="hidden sm:inline">Refresh</span>
        </button>
      </div>

      {/* Station Selector + Controls */}
      <div className="flex items-center gap-4 flex-wrap">
        {/* Location selector */}
        <div className="flex items-center gap-2 bg-bg-card rounded-lg p-1 border border-text-muted/20">
          {gasLocations.map((location) => (
            <button
              key={location.id}
              onClick={() => setSelectedLocationId(location.id)}
              className={`px-4 py-2 rounded text-sm font-medium transition-all ${
                selectedLocationId === location.id
                  ? 'bg-jrd-orange text-white'
                  : 'text-text-secondary hover:text-text-primary'
              }`}
            >
              {location.name}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <span className="text-text-muted text-sm">Strategy:</span>
          <select
            value={pricingStrategy}
            onChange={(e) => setPricingStrategy(e.target.value as typeof pricingStrategy)}
            className="bg-bg-input border border-text-muted/30 rounded px-3 py-1.5 text-sm"
          >
            <option value="match">Match</option>
            <option value="undercut">Undercut</option>
            <option value="premium">Premium</option>
          </select>
        </div>

        {/* Email config button */}
        <button
          onClick={() => setShowEmailConfig(true)}
          className={`btn-ghost flex items-center gap-2 ${
            emailConfigs[selectedLocationId || ''] ? 'text-jrd-green' : ''
          }`}
        >
          <Mail className="w-4 h-4" />
          <span className="hidden sm:inline">Price Notices</span>
        </button>

        {/* Price Simulator button */}
        <button
          onClick={() => setShowSimulator(true)}
          className="btn-primary flex items-center gap-2"
        >
          <Calculator className="w-4 h-4" />
          <span className="hidden sm:inline">Simulator</span>
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Your Prices Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card lg:col-span-2"
        >
          <div className="card-header flex items-center justify-between">
            <span>// YOUR PRICES</span>
            <span className="text-jrd-green text-xs flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {pricesLastUpdated ? formatRelativeTime(pricesLastUpdated) : 'Not loaded'}
            </span>
          </div>
          <div className="card-body">
            {/* Loading state */}
            {isLoadingPrices && !currentPrices && (
              <div className="text-center py-8 text-text-muted">
                <RefreshCw className="w-8 h-8 mx-auto mb-3 animate-spin" />
                <p>Fetching prices from GasBuddy...</p>
              </div>
            )}

            {/* No GasBuddy ID */}
            {!selectedLocation?.gasbuddyId && (
              <div className="text-center py-8 text-text-muted">
                <AlertTriangle className="w-8 h-8 mx-auto mb-3 text-yellow-500" />
                <p className="mb-2">No GasBuddy ID configured for this location</p>
                <a href="/settings" className="text-jrd-orange text-sm hover:underline">
                  Configure in Settings
                </a>
              </div>
            )}

            {/* Prices display */}
            {currentPrices && (
              <>
                <div className="grid grid-cols-3 gap-4">
                  {(['87', '89', '91'] as const).map((grade) => {
                    const priceData = currentPrices.prices.find((p) => p.grade === grade);
                    const wholesale = currentWholesale?.grade === grade ? currentWholesale.cost : null;
                    const gradeMargin = priceData && wholesale ? priceData.price - wholesale : null;

                    return (
                      <div
                        key={grade}
                        className={`p-4 rounded-lg border ${
                          grade === '87'
                            ? 'bg-jrd-orange/5 border-jrd-orange/30'
                            : 'bg-bg-input border-text-muted/20'
                        }`}
                      >
                        <div className="text-text-muted text-xs font-mono mb-1">
                          {grade === '87' ? '87 UNLEADED' : grade.toUpperCase()}
                        </div>
                        <div className="text-2xl font-bold text-text-primary lcd-display">
                          {priceData ? formatFuelPrice(priceData.price) : '--.-⁹'}
                        </div>
                        {grade === '87' && margin87 > 0 && (
                          <div className="mt-2">
                            <div className="text-xs text-text-muted">MARGIN</div>
                            <div className="text-lg font-bold text-jrd-green">
                              {formatMargin(margin87)}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Margin bar for 87 */}
                <div className="mt-6 p-4 bg-bg-input rounded-lg border border-text-muted/20">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-text-secondary text-sm">87 Unleaded Margin</span>
                    <span className={`font-mono font-bold ${margin87 > 0 ? 'text-jrd-green' : 'text-text-muted'}`}>
                      {margin87 > 0 ? formatMargin(margin87) : 'No wholesale cost'}
                    </span>
                  </div>
                  <div className="h-4 bg-bg-darker rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${Math.min((margin87 / 1.5) * 100, 100)}%` }}
                      transition={{ duration: 1, ease: 'easeOut' }}
                      className="h-full bg-gradient-to-r from-jrd-orange to-jrd-green"
                    />
                  </div>
                  <div className="flex items-center justify-between mt-2 text-xs text-text-muted">
                    <span>$0.00</span>
                    <span className="text-jrd-green">Target: $1.00+</span>
                    <span>$1.50</span>
                  </div>
                </div>

                {/* Wholesale info */}
                <div className="mt-4 flex items-center justify-between p-3 bg-bg-input rounded border border-text-muted/20">
                  <div>
                    <div className="text-text-muted text-xs">WHOLESALE COST (87)</div>
                    <div className="text-text-primary font-mono">
                      {currentWholesale ? `$${currentWholesale.cost.toFixed(3)}/gal` : 'Not set'}
                    </div>
                    {currentWholesale && (
                      <div className="text-text-muted text-xs mt-1">
                        Effective: {new Date(currentWholesale.effectiveDate).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="text-text-muted text-xs">SUPPLIER</div>
                    <div className="text-jrd-orange text-sm">
                      {currentWholesale?.supplier || 'Unknown'}
                    </div>
                  </div>
                </div>

                {/* Upload price notice */}
                <div className="mt-4">
                  <label className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-text-muted/30 rounded-lg hover:border-jrd-orange/50 transition-colors cursor-pointer">
                    <Upload className="w-4 h-4 text-text-muted" />
                    <span className="text-text-secondary text-sm">
                      Upload Price Notice (PDF/TXT)
                    </span>
                    <input
                      type="file"
                      accept=".pdf,.txt,.csv"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                  </label>

                  {/* Upload status */}
                  <AnimatePresence>
                    {uploadStatus !== 'idle' && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className={`mt-2 p-2 rounded text-sm flex items-center gap-2 ${
                          uploadStatus === 'success'
                            ? 'bg-green-500/10 text-green-400'
                            : uploadStatus === 'error'
                            ? 'bg-red-500/10 text-red-400'
                            : 'bg-blue-500/10 text-blue-400'
                        }`}
                      >
                        {uploadStatus === 'processing' && (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        )}
                        {uploadStatus === 'success' && <CheckCircle className="w-4 h-4" />}
                        {uploadStatus === 'error' && <AlertTriangle className="w-4 h-4" />}
                        {uploadMessage}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </>
            )}
          </div>
        </motion.div>

        {/* Competitors Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card"
        >
          <div className="card-header flex items-center justify-between">
            <span>// COMPETITORS (10mi)</span>
            <div className="flex items-center gap-2">
              <span className="text-text-muted text-xs">
                {competitors.length} stations
              </span>
              <Filter className="w-4 h-4 text-text-muted" />
            </div>
          </div>
          <div className="card-body">
            {/* Your price header */}
            <div className="mb-4 p-3 bg-jrd-orange/10 rounded border border-jrd-orange/30">
              <div className="text-text-muted text-xs mb-1">YOUR PRICE (87)</div>
              <div className="text-2xl font-bold text-jrd-orange">
                {retailPrice87 > 0 ? formatFuelPrice(retailPrice87) : '--.-⁹'}
              </div>
            </div>

            {/* Loading */}
            {isLoadingCompetitors && competitors.length === 0 && (
              <div className="text-center py-8 text-text-muted">
                <RefreshCw className="w-6 h-6 mx-auto mb-2 animate-spin" />
                <p className="text-sm">Loading competitors...</p>
              </div>
            )}

            {/* No competitors */}
            {!isLoadingCompetitors && competitors.length === 0 && (
              <div className="text-center py-8 text-text-muted">
                <MapPin className="w-8 h-8 mx-auto mb-3 opacity-50" />
                <p className="text-sm">No competitors found nearby</p>
                <p className="text-xs mt-1">Add location coordinates in Settings</p>
              </div>
            )}

            {/* Competitor list */}
            <div className="space-y-2 max-h-80 overflow-y-auto scrollbar-hide">
              {competitors.map((competitor) => {
                const their87 = competitor.prices.find((p) => p.grade === '87')?.price || 0;
                const color =
                  their87 > 0 && retailPrice87 > 0
                    ? getCompetitorColor(retailPrice87, their87, pricingStrategy)
                    : 'yellow';
                const diff = their87 > 0 && retailPrice87 > 0 ? their87 - retailPrice87 : 0;
                const diffStr =
                  diff === 0 ? 'MATCH' : `${diff > 0 ? '+' : ''}$${diff.toFixed(3)}`;

                return (
                  <div
                    key={competitor.id}
                    className={`p-3 rounded border ${
                      color === 'green'
                        ? 'bg-green-500/5 border-green-500/30'
                        : color === 'red'
                        ? 'bg-red-500/5 border-red-500/30'
                        : 'bg-yellow-500/5 border-yellow-500/30'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-text-primary text-sm font-medium">
                          {competitor.name}
                        </div>
                        <div className="flex items-center gap-1 text-text-muted text-xs">
                          <MapPin className="w-3 h-3" />
                          {competitor.distance ? formatDistance(competitor.distance) : 'N/A'}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-text-primary font-mono">
                          {their87 > 0 ? formatFuelPrice(their87) : '--.-⁹'}
                        </div>
                        {their87 > 0 && retailPrice87 > 0 && (
                          <div
                            className={`text-xs font-mono ${
                              color === 'green'
                                ? 'text-green-400'
                                : color === 'red'
                                ? 'text-red-400'
                                : 'text-yellow-400'
                            }`}
                          >
                            {diffStr}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Last updated */}
            {competitorsLastUpdated && (
              <div className="mt-3 text-xs text-text-muted text-center">
                Updated {formatRelativeTime(competitorsLastUpdated)}
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Email config modal */}
      {selectedLocationId && (
        <EmailConfigModal
          isOpen={showEmailConfig}
          onClose={() => setShowEmailConfig(false)}
          locationId={selectedLocationId}
        />
      )}

      {/* Price Simulator modal */}
      <PriceSimulatorModal
        isOpen={showSimulator}
        onClose={() => setShowSimulator(false)}
        currentPrices={currentPrices?.prices || []}
        wholesaleCost={wholesaleCost87}
      />
    </div>
  );
}
